import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAgents, useDeleteAgent, useUpdateAgentStatus, useDuplicateAgent } from '../hooks';
import { AgentCard } from '../components/AgentCard';
import { AgentListItem } from '../components/AgentListItem';
import { AgentCreationDrawer } from '../components/creation/AgentCreationDrawer';
import {
  FiltersButton,
  Button,
  DeleteConfirmationModal,
  FilterBottomSheet,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SortableHeadCell,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { AIAgentQuery } from '../services/agentsApi';
import type { AIAgentResponse, ActiveStatus } from '@troia-v3/types';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { scoreColor } from '../components/agent-visuals';
import type { AgentChannelInfo, AgentWithScore } from '../components/agent-visuals';

type SortKey = 'name' | 'model' | 'status' | 'quality';

export const AIAgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<AIAgentQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modelFilter, setModelFilter] = useState<string | undefined>(undefined);
  const [showCreationDrawer, setShowCreationDrawer] = useState(false);
  // Exclusão via modal (mockup #mdlDelete) — substitui window.confirm.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // "+" central do bottom-nav mobile → novo agente (modo prático), mockup `.m-fab`.
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); setShowCreationDrawer(true); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  // Rota aninhada (formulário completo de edição/criação) — renderiza só o Outlet.
  const isNestedRoute = location.pathname.includes('/edit') || location.pathname.includes('/new');

  const { data, isLoading, error } = useAgents(query);
  // Canais pra resolver os círculos `.ag-chs` dos cards/linhas (rota dropdown cross-module)
  const { data: channelsData } = useChannelsDropdown();
  const channelsById = useMemo(() => {
    const map = new Map<string, AgentChannelInfo>();
    for (const ch of channelsData ?? []) {
      map.set(ch.id, { id: ch.id, name: ch.name, ...(ch.providerId ? { providerId: ch.providerId } : {}) });
    }
    return map;
  }, [channelsData]);
  const deleteAgent = useDeleteAgent();
  const updateAgentStatus = useUpdateAgentStatus();
  const duplicateAgent = useDuplicateAgent();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Métricas (KPIs) — calculadas sobre a página carregada (comportamento preservado da AgentsSidebar).
  const metrics = useMemo(() => {
    const activeAgents = items.filter((a) => a.status === 'active').length;
    const inactiveAgents = items.filter((a) => a.status === 'inactive').length;
    const totalInteractions = items.reduce((sum, a) => sum + (a.totalInteractions || 0), 0);
    const modelCounts = items.reduce((acc, a) => {
      acc[a.model] = (acc[a.model] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const modelDistribution = Object.entries(modelCounts)
      .map(([model, count]) => ({ model, count }))
      .sort((a, b) => b.count - a.count);
    // Qualidade média (KPI do mockup): média dos agentes COM score (30d)
    const scores = items
      .map((a) => (a as AgentWithScore).qualityScore)
      .filter((v): v is number => v !== undefined);
    const avgQuality = scores.length > 0
      ? Math.round(scores.reduce((sum, v) => sum + v, 0) / scores.length)
      : undefined;
    return { activeAgents, inactiveAgents, totalInteractions, modelDistribution, avgQuality };
  }, [items]);

  // Modelos distintos na página carregada (opções do filtro client-side de modelo).
  const distinctModels = useMemo(
    () => Array.from(new Set(items.map((a) => a.model))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items],
  );

  // Filtro de modelo é client-side (backend AIAgentQuery não expõe `model`).
  const visibleItems = useMemo(
    () => (modelFilter ? items.filter((a) => a.model === modelFilter) : items),
    [items, modelFilter],
  );

  // Ordenação client-side na página carregada (backend não expõe sort de agentes).
  const sortedItems = useMemo(() => {
    const copy = [...visibleItems];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'model') cmp = a.model.localeCompare(b.model, 'pt-BR');
      else if (sort.key === 'status') cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      else cmp = ((a as AgentWithScore).qualityScore ?? -1) - ((b as AgentWithScore).qualityScore ?? -1);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [visibleItems, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = visibleItems.length > 0 && visibleItems.every((a) => selectedIds.has(a.id));
  const someOnPageSelected = visibleItems.some((a) => selectedIds.has(a.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ActiveStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const setModelFilterValue = (model?: string) => {
    setModelFilter(model);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, page: 1 }));
    setModelFilter(undefined);
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) visibleItems.forEach((a) => next.delete(a.id));
      else visibleItems.forEach((a) => next.add(a.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleEdit = (id: string) => navigate(`/automation/ai-agents/${id}/edit`);

  const handleDelete = (id: string) => {
    const agent = visibleItems.find((a) => a.id === id);
    setDeleteTarget({ id, name: agent?.name || 'este agente' });
  };

  const confirmDelete = () => {
    if (deleteTarget) deleteAgent.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleToggleStatus = (id: string, currentStatus: string) => {
    const newStatus: ActiveStatus = currentStatus === 'active' ? 'inactive' : 'active';
    updateAgentStatus.mutate({ id, status: newStatus });
  };

  const handleDuplicate = (id: string) => duplicateAgent.mutate(id);

  const bulkDuplicate = () => {
    Array.from(selectedIds).forEach((id) => duplicateAgent.mutate(id));
    clearSelection();
  };

  const bulkActivate = () => {
    visibleItems
      .filter((a) => selectedIds.has(a.id) && a.status !== 'active')
      .forEach((a) => updateAgentStatus.mutate({ id: a.id, status: 'active' }));
    clearSelection();
  };

  const bulkDeactivate = () => {
    visibleItems
      .filter((a) => selectedIds.has(a.id) && a.status === 'active')
      .forEach((a) => updateAgentStatus.mutate({ id: a.id, status: 'inactive' }));
    clearSelection();
  };

  const bulkDelete = () => setBulkDeleteOpen(true);

  const confirmBulkDelete = () => {
    Array.from(selectedIds).forEach((id) => deleteAgent.mutate(id));
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const statusLabel = query.status === 'active' ? 'Ativos' : query.status === 'inactive' ? 'Pausados' : null;
  const activeFilterCount = [statusLabel, modelFilter].filter(Boolean).length;

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todos'], ['active', 'Ativos'], ['inactive', 'Pausados']] as const).map(([val, label]) => {
        const active = query.status === val;
        return (
          <button
            key={label}
            onClick={() => setStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      {/* Modelo (filtro client-side sobre a página carregada) */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Modelo</p>
      <button
        onClick={() => setModelFilterValue(undefined)}
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
        style={{ color: '#374151', backgroundColor: !modelFilter ? '#f3e8ff' : 'transparent' }}
        onMouseEnter={(e) => { if (modelFilter) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={(e) => { if (modelFilter) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        Todos
        {!modelFilter && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
      </button>
      {distinctModels.map((model) => {
        const active = modelFilter === model;
        return (
          <button
            key={model}
            onClick={() => setModelFilterValue(model)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="truncate">{model}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
      {distinctModels.length === 0 && (
        <p className="px-2.5 py-2 text-xs" style={{ color: '#94a3b8' }}>Nenhum modelo na página atual</p>
      )}
    </>
  );

  // Rota aninhada: o formulário completo (com sua própria sidebar de chat) cuida do layout.
  if (isNestedRoute) return <Outlet />;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          /* Esquerda: breadcrumb + contador */
          <>
          <nav className="flex items-center gap-2 min-w-0">
            {/* Mockup ai-agents.html: zona esquerda mostra SÓ o título (16px/700) */}
            <span className="font-bold text-base truncate flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
              <Icon name="cpu" className="w-4 h-4" />
              Agentes de IA
            </span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar agentes…"
            shrinkClass="ds-search"
            data-testid="agents-search"
          />
        }
        right={
          /* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTAs */
          <>
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="agents-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="agents-view"
          />

          {/* .tb-sep antes dos CTAs */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Modo avançado (formulário completo) — secundário */}
          <Button
            variant="secondary"
            icon="sliders"
            collapsibleLabel
            onClick={() => navigate('/automation/ai-agents/new')}
            data-testid="new-agent-advanced"
          >
            Modo avançado
          </Button>

          {/* Novo agente (modo prático — único primário, F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => setShowCreationDrawer(true)}
            data-testid="new-agent"
          >
            Novo agente
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar agente…"
              data-testid="agents-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="agents-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center gap-2 px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold flex-none whitespace-nowrap" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {modelFilter && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold flex-none whitespace-nowrap" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Modelo: {modelFilter}
              <button onClick={() => setModelFilterValue(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de modelo">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold flex-none whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }} data-testid="agents-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar agentes: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {/* ── Bulk bar ── */}
          {selectedCount > 0 && (
            <div className="flex items-center gap-3 px-3.5 py-2 mb-3 rounded-lg" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              <span className="font-bold text-sm">{selectedCount} selecionado(s)</span>
              <span className="flex-1" />
              <button onClick={bulkActivate} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: 'var(--theme-primary)' }} data-testid="agents-bulk-activate">
                <Icon name="check-circle" className="w-3.5 h-3.5" /> Ativar
              </button>
              <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: 'var(--theme-primary)' }} data-testid="agents-bulk-deactivate">
                <Icon name="power" className="w-3.5 h-3.5" /> Pausar
              </button>
              <button onClick={bulkDuplicate} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: 'var(--theme-primary)' }} data-testid="agents-bulk-duplicate">
                <Icon name="copy" className="w-3.5 h-3.5" /> Duplicar
              </button>
              <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: 'var(--theme-primary)' }} data-testid="agents-bulk-delete">
                <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
              </button>
              <button onClick={clearSelection} className="text-sm font-semibold hover:underline" style={{ color: 'var(--theme-primary)' }} data-testid="agents-bulk-cancel">Cancelar</button>
            </div>
          )}

          {/* KPIs (métricas da antiga AgentsSidebar) */}
          {!isLoading && !error && items.length > 0 && (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mb-4">
              <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}>
                <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="activity" className="w-3.5 h-3.5" /> Total de interações
                </div>
                <div className="text-2xl font-extrabold mt-1.5 tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
                  {metrics.totalInteractions.toLocaleString('pt-BR')}
                </div>
              </div>
              <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}>
                <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="cpu" className="w-3.5 h-3.5" /> Status dos agentes
                </div>
                <div className="text-2xl font-extrabold mt-1.5 tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
                  {metrics.activeAgents}
                  <span className="text-sm font-semibold" style={{ color: 'var(--theme-muted-text)' }}> ativos · </span>
                  {metrics.inactiveAgents}
                  <span className="text-sm font-semibold" style={{ color: 'var(--theme-muted-text)' }}> pausados</span>
                </div>
              </div>
              <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}>
                <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="thermometer" className="w-3.5 h-3.5" /> Qualidade média
                </div>
                <div
                  className="text-2xl font-extrabold mt-1.5 tabular-nums"
                  style={{ color: metrics.avgQuality !== undefined ? scoreColor(metrics.avgQuality) : 'var(--theme-muted-text)' }}
                >
                  {metrics.avgQuality !== undefined ? (
                    <>
                      {metrics.avgQuality}
                      <span className="text-sm font-semibold" style={{ color: 'var(--theme-muted-text)' }}>%</span>
                    </>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}>
                <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="bar-chart" className="w-3.5 h-3.5" /> Modelos usados
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  {metrics.modelDistribution.slice(0, 4).map((m) => (
                    <span key={m.model}>
                      <b style={{ color: 'var(--theme-card-text)' }}>{m.count}</b> {m.model}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="cpu"
              message={searchTerm || activeFilterCount > 0 ? 'Nenhum agente encontrado' : 'Nenhum agente ainda'}
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Selecione um agente ou crie um novo para começar a automatizar seu atendimento.'}
              actionLabel="Modo prático"
              onAction={() => setShowCreationDrawer(true)}
              secondaryActionLabel="Modo avançado"
              onSecondaryAction={() => navigate('/automation/ai-agents/new')}
            />
          )}

          {/* Filtro de modelo (client-side) esvaziou a página atual */}
          {!isLoading && !error && items.length > 0 && visibleItems.length === 0 && (
            <ListPageEmpty
              icon="filter"
              message="Nenhum agente para esse filtro"
              description="O filtro de modelo não encontrou agentes nesta página."
              actionLabel="Limpar filtros"
              onAction={clearAllFilters}
            />
          )}

          {/* Grid (Cards) — F34: sem checkbox de seleção em cards */}
          {!isLoading && !error && visibleItems.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(268px,1fr))] gap-3">
              {sortedItems.map((agent: AIAgentResponse) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  channelsById={channelsById}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onDuplicate={handleDuplicate}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && visibleItems.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((agent: AIAgentResponse) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    channelsById={channelsById}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    onDuplicate={handleDuplicate}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" style={{ minWidth: 860 }}>
                    <thead>
                      {/* F43: head padrão (SortableHeadCell text-xs, sem uppercase/fundo sunken) */}
                      <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                        <SelectAllHeadCell
                          checked={allOnPageSelected}
                          indeterminate={someOnPageSelected && !allOnPageSelected}
                          onToggle={toggleSelectAllOnPage}
                          data-testid="agents-select-all"
                        />
                        <SortableHeadCell label="Agente" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="agents-sort-name" />
                        <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="agents-sort-status" />
                        <SortableHeadCell label="Modelo" sortKey="model" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="agents-sort-model" />
                        <PlainHeadCell label="Canais" />
                        <SortableHeadCell label="Qualidade" sortKey="quality" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="agents-sort-quality" />
                        <PlainHeadCell className="w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((agent: AIAgentResponse) => (
                        <AgentListItem
                          key={agent.id}
                          agent={agent}
                          channelsById={channelsById}
                          selected={selectedIds.has(agent.id)}
                          onToggleSelect={toggleSelect}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onToggleStatus={handleToggleStatus}
                          onDuplicate={handleDuplicate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Paginação (F49 — padrão completo "Mostrando X–Y de N") */}
          {!isLoading && !error && visibleItems.length > 0 && (
            <ListPagePagination
              currentPage={data?.page ?? query.page ?? 1}
              totalPages={data?.totalPages ?? 1}
              onPageChange={(p) => setQuery((prev) => ({ ...prev, page: p }))}
              totalItems={data?.total ?? visibleItems.length}
              pageSize={data?.limit ?? query.limit ?? 12}
              itemLabel="agentes"
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="agents-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      <AgentCreationDrawer
        isOpen={showCreationDrawer}
        onClose={() => setShowCreationDrawer(false)}
      />

      {/* Exclusão (mockup #mdlDelete) — modal estilizado no lugar de window.confirm */}
      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Excluir agente"
        message={`Tem certeza que deseja excluir "${deleteTarget?.name ?? ''}"?`}
        warningMessage="Isso remove permanentemente o agente e suas configurações."
        isLoading={deleteAgent.isPending}
      />
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Excluir agentes"
        message={`Tem certeza que deseja excluir ${selectedCount} agente(s) selecionado(s)?`}
        warningMessage="Isso remove permanentemente os agentes e suas configurações."
        isLoading={deleteAgent.isPending}
      />
    </div>
  );
};
